// ============================================================================
// cabinet/tabs.ts — РЕЕСТР ВКЛАДОК «КАБИНЕТА» (SSoT клиента).
//
// «Кабинет» — приложение (kind='app') с личной точкой входа. Состав вкладок
// определяется РЕЕСТРОМ, а не условиями в оболочке: каждая вкладка приходит
// со своим кодом, подписью, иконкой и компонентом. Оболочка (CabinetShell)
// только рисует список и переключает активную вкладку — новых вкладок она
// не знает.
//
// Расширение: модуль/приложение регистрирует вкладку через registerCabinetTab
// при старте клиента (рядом с registerAppRenderer). Позже тот же реестр сможет
// наполняться с сервера (модуль-поставщик, право, порядок) — оболочку менять
// не потребуется.
// ============================================================================

import type { ComponentType } from 'react';

/** Код приложения «Кабинет» (рендерер регистрируется клиентом). */
export const CABINET_APP_CODE = 'cabinet';

/** Название по умолчанию (позже — настройка базы/роли: «Мой кабинет», «Мой офис»). */
export const CABINET_TITLE = 'Кабинет';

/** Иконка вкладки (структурный тип: подходит любая lucide-иконка). */
export type CabinetTabIcon = ComponentType<{ className?: string }>;

export interface CabinetTab {
  /** Стабильный код вкладки (например, 'support'). */
  code: string;
  /** Подпись в полосе вкладок (русская, для людей). */
  title: string;
  /** Иконка вкладки. */
  icon: CabinetTabIcon;
  /** Порядок в полосе: меньше — раньше; при равенстве — по названию. */
  order?: number;
  /** Содержимое вкладки (собственная разметка/страница). */
  Component: ComponentType;
  /** Короткое пояснение (подсказка при наведении), необязательно. */
  description?: string;
}

const registry = new Map<string, CabinetTab>();

// Подписка на изменения реестра: оболочка перерисовывается, если вкладку
// добавили/сняли уже после её монтирования (модуль/поставка).
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribeCabinetTabs(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Версия реестра — снимок для useSyncExternalStore (оболочка). */
export function cabinetTabsVersion(): number {
  return version;
}

/**
 * registerCabinetTab — добавить вкладку в реестр.
 * Дубликат кода — ошибка (детерминированно, без тихой подмены).
 */
export function registerCabinetTab(tab: CabinetTab): void {
  const code = tab.code.trim();
  if (!code) throw new Error('cabinet tab: пустой код вкладки');
  if (!tab.title.trim()) throw new Error(`cabinet tab '${code}': пустая подпись`);
  if (registry.has(code)) throw new Error(`cabinet tab '${code}' уже зарегистрирована`);
  registry.set(code, { ...tab, code });
  notify();
}

/** getCabinetTab — вкладка по коду (или undefined, если не зарегистрирована). */
export function getCabinetTab(code: string): CabinetTab | undefined {
  return registry.get(code);
}

/** listCabinetTabs — все вкладки в порядке отображения. */
export function listCabinetTabs(): CabinetTab[] {
  return Array.from(registry.values()).sort((a, b) => {
    const byOrder = (a.order ?? 100) - (b.order ?? 100);
    if (byOrder !== 0) return byOrder;
    return a.title.localeCompare(b.title, 'ru');
  });
}
